import { useFormikContext } from 'formik';
import * as React from 'react';
import { Button, Modal, Overlay, Popover } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  addRowToPayload,
  DataPills,
  Display,
  fetchApi,
  FormBlock,
  FormManager,
  GCacheRecord,
  List,
  makeCompoundPayload,
  makeResetCache,
  notifyError,
  notifyResponse,
  PageTitle,
  ParallelLoad,
  QueryMulti,
  QuerySingle,
  Section,
  useCacheRecord,
  useCloneEntity,
} from 'gossamer-front-end';
import { isGroup, isRoot, Task, TaskDefinition, TaskDefinitionRow } from 'gossamer-model';
import { TreeNode } from 'gossamer-front-end';
import styles from './WorkflowDisplay.module.css';
import { getFilter, getRowKey, OptionsField, truncate } from 'gossamer-unified';
import { DisplayMarkdown } from 'shared-front-end';
import { WorkflowCopyButton } from './WorkflowCopyButton';
import { TaskDefnNode } from './TaskDefnNode';
import { TaskDefnView } from 'cb-model';

export const WorkflowDisplay = (): React.JSX.Element => {
  const params = useParams();
  const taskRecord = useCacheRecord(TaskDefinition, params.key);

  return (
    <>
      <PageTitle verb="Display" entity={TaskDefinition} keyValue={params.key} />
      {isRoot(taskRecord.data?.type) && (
        <Section>
          <WorkflowTree taskRecord={taskRecord} />
          <WorkflowCopyButton taskDefId={Number(params.key)} />
        </Section>
      )}
      {!!taskRecord.data && !isRoot(taskRecord.data?.type) && (
        <Section>
          <QuerySingle entity={TaskDefinition} keyValue={params.key} render={Display} />
        </Section>
      )}
      <Section title="Progress Overview">
        <QuerySingle entity={TaskDefnView} keyValue={params.key} render={Display} fieldGroup="summary" />
        <QueryMulti
          entity={TaskDefnView}
          render={List}
          queryOptions={{
            where: {
              and: [getFilter('parent_definition_id', 'eq', params.key)],
            },
          }}
        />
      </Section>
      {!isGroup(taskRecord.data?.type) && (
        <Section title="Users for whom this Task is Active">
          <QueryMulti
            entity={Task}
            render={(innerProps) => (
              <DataPills fieldId="assigned_to" entity={innerProps.entity} rows={innerProps.rows} />
            )}
            queryOptions={{
              where: {
                and: [getFilter('definition_id', 'eq', params.key), getFilter('status', 'eq', 'ACTIVE')],
              },
            }}
          />
        </Section>
      )}
      {!isGroup(taskRecord.data?.type) && (
        <Section title="Users for whom this Task is Completed">
          <QueryMulti
            entity={Task}
            render={(innerProps) => (
              <DataPills fieldId="assigned_to" entity={innerProps.entity} rows={innerProps.rows} />
            )}
            queryOptions={{
              where: {
                and: [getFilter('definition_id', 'eq', params.key), getFilter('status', 'eq', 'COMPLETED')],
              },
            }}
          />
        </Section>
      )}
    </>
  );
};

interface WorkflowTreeProps {
  taskRecord: GCacheRecord<TaskDefinitionRow>;
}

const WorkflowTree = (props: WorkflowTreeProps) => {
  const [showModal, setShowModal] = React.useState(false);
  const [currentNodeId, setCurrentNodeId] = React.useState<number | null>(null);
  const [parentNode, setParentNode] = React.useState<TaskDefnNode | null>(null);
  const taskDefnTree = React.useRef<TaskDefnNode>(null);
  const dispatch = useDispatch();
  const resetCache = makeResetCache(dispatch);

  React.useEffect(() => {
    if (props.taskRecord.data) {
      taskDefnTree.current = new TaskDefnNode(props.taskRecord.data.id, props.taskRecord.data.seq);
    }
  }, [props.taskRecord.data]);

  const closeModal = () => {
    setCurrentNodeId(null);
    setParentNode(null);
    setShowModal(false);
  };

  const handleCreateClick = (row: TaskDefinitionRow) => {
    setCurrentNodeId(null);
    setParentNode(taskDefnTree.current?.getNodeById(row.id) ?? null);
    setShowModal(true);
  };

  const handleDeleteClick = async (row: TaskDefinitionRow) => {
    const conf = confirm(`Delete the item '${row.label}'?`);
    if (!conf) {
      return;
    }
    const payload = makeCompoundPayload('save');
    const key = getRowKey(TaskDefinition, row);
    addRowToPayload(payload, TaskDefinition, { ...row, _original_key: key, _action: 'D' });
    try {
      const resp = await fetchApi('POST', `/main/~/delete-task-definition`, payload);
      resetCache(TaskDefinition.id, key);
      notifyResponse(resp);
    } catch (e) {
      notifyError(e, 'An error occurred while deleting this item');
    }
  };

  const handleEditClick = (row: TaskDefinitionRow) => {
    setCurrentNodeId(row.id);
    setParentNode(null);
    setShowModal(true);
  };

  const handleSwapWithNeighbour = async (row: TaskDefinitionRow, neighbour: 'next' | 'previous') => {
    const thisNode = taskDefnTree.current?.getNodeById(row.id);
    if (!thisNode) {
      notifyError(`[TaskDefnTree] Unable to find the item in the current tree`, 'Unable to move item');
      return;
    }
    const neighbourNode = thisNode.getNeighbour(neighbour);
    console.log(
      `[TaskDefnTree] ${row.id} having seq ${row.seq} with ${neighbour} neighbour: ${neighbourNode?.id} having seq ${neighbourNode?.seq}`,
    );
    if (!neighbourNode) {
      notifyError(`[TaskDefnTree] No ${neighbour} neighbour to swap with`, 'Unable to move item');
      return;
    }
    const payload = makeCompoundPayload('save');
    const key = getRowKey(TaskDefinition, row);
    addRowToPayload(payload, TaskDefinition, { id: row.id, seq: neighbourNode.seq, _original_key: key, _action: 'U' });
    addRowToPayload(payload, TaskDefinition, {
      id: neighbourNode.id,
      seq: row.seq,
      _original_key: String(neighbourNode.id),
      _action: 'U',
    });
    try {
      const resp = await fetchApi('POST', `/main/~/swap-task-definition-seq`, payload);
      const thisNodeSeq = thisNode.seq;
      thisNode.seq = neighbourNode.seq;
      neighbourNode.seq = thisNodeSeq;
      resetCache(TaskDefinition.id, key);
      resetCache(TaskDefinition.id, String(neighbourNode.id));
      notifyResponse(resp);
    } catch (e) {
      notifyError(e, 'An error occurred while updating this item');
    }
  };

  return (
    <div className={styles.WorkflowDisplay}>
      <ParallelLoad responses={[props.taskRecord]}>
        <TreeNode
          entity={TaskDefinition}
          initiallyExpanded={(row) => {
            if (isRoot(row.type)) {
              return true;
            }
            return taskDefnTree.current?.getNodeById(row.id)?.isExpanded ?? false;
          }}
          isExpandable={(row) => isGroup(row.type)}
          onExpand={(row) => {
            if (taskDefnTree.current) {
              taskDefnTree.current.getNodeById(row.id)!.isExpanded = true;
            }
          }}
          onContract={(row) => {
            if (taskDefnTree.current) {
              taskDefnTree.current.getNodeById(row.id)!.isExpanded = false;
            }
          }}
          row={props.taskRecord.data as TaskDefinitionRow}
          render={(innerProps) => {
            if (taskDefnTree.current) {
              taskDefnTree.current.addIfNew(innerProps.row.id, innerProps.row.seq, innerProps.row.parent_definition_id);
            }
            return (
              <RenderRow
                row={innerProps.row}
                index={innerProps.index}
                isLast={innerProps.isLast}
                handleCreateClick={handleCreateClick}
                handleDeleteClick={handleDeleteClick}
                handleEditClick={handleEditClick}
                handleSwapWithNeighbour={handleSwapWithNeighbour}
              />
            );
          }}
          childQueryOptions={{
            where: {
              and: [{ fieldId: 'parent_definition_id', operator: 'eq', value: '$PARENT' }],
            },
            order: [['seq', 'ASC']],
            limit: 100,
          }}
        />
      </ParallelLoad>
      Legend:
      <ul>
        <li>
          <img src="/assets/icons/play-pause.svg" />
          <b>Sequential Group</b> - items within the group are activated one-by-one, and the group is complete when all
          items are complete
        </li>
        <li>
          <img src="/assets/icons/forward.svg" />
          <b>Parallel Group</b> - items within the group are activated simultaneously, and the group is complete when
          all items are complete
        </li>
        {/* <li>
              <img src="/assets/icons/puzzle-piece.svg" />
              <b>Application Task</b> - the item is an action to be performed by the user in the system, and is complete
              when saved
            </li> */}
        <li>
          <img src="/assets/icons/clipboard-document-check.svg" />
          <b>Check Task</b> - the item is a block of text to be read by the user, who then clicks "Done" to confirm and
          complete the item
        </li>
      </ul>
      <UpsertTaskDefinitionModal
        institutionId={props.taskRecord.data?.institution_id}
        currentNodeId={currentNodeId}
        parentNode={parentNode}
        show={showModal}
        closeModal={closeModal}
      />
    </div>
  );
};

const TYPE_ICON: Record<string, string> = {
  ROOT: '/assets/icons/play-pause.svg', // visually same as sequential group
  'SEQUENTIAL-GROUP': '/assets/icons/play-pause.svg',
  'PARALLEL-GROUP': '/assets/icons/forward.svg',
  'APPLICATION-TASK': '/assets/icons/puzzle-piece.svg',
  'CHECK-TASK': '/assets/icons/clipboard-document-check.svg',
};

const TYPE_TEXT: Record<string, string> = {
  ROOT: 'Sequential Group',
  'SEQUENTIAL-GROUP': 'Sequential Group',
  'PARALLEL-GROUP': 'Parallel Group',
  'APPLICATION-TASK': 'Application Task',
  'CHECK-TASK': 'Check Task',
};

interface RenderRowProps {
  handleCreateClick: (parentRow: TaskDefinitionRow) => void;
  handleDeleteClick: (row: TaskDefinitionRow) => void;
  handleEditClick: (row: TaskDefinitionRow) => void;
  handleSwapWithNeighbour: (row: TaskDefinitionRow, neighbour: 'next' | 'previous') => void;
  index?: number;
  isLast?: boolean;
  row: TaskDefinitionRow;
}

const RenderRow = (props: RenderRowProps): React.JSX.Element => {
  const [show, setShow] = React.useState(false);
  const target = React.useRef(null);
  const markdownTruncated = truncate(props.row.markdown_text, 300);

  return (
    <span>
      <a onClick={() => setShow(!show)} ref={target} title={TYPE_TEXT[props.row.type]} className="control">
        <img src={TYPE_ICON[props.row.type]} />
      </a>
      <Overlay
        show={show}
        target={target}
        placement="right"
        containerPadding={20}
        rootClose={true}
        onHide={() => setShow(false)}
      >
        <Popover id="popover-basic">
          <Popover.Header as="h3">{props.row.label}</Popover.Header>
          <Popover.Body>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {props.row.type === 'CHECK-TASK' && <DisplayMarkdown source={markdownTruncated} />}
              <div style={{ display: 'flex', flexFlow: 'row wrap', gap: '8px' }}>
                {!isRoot(props.row.type) && (
                  <Button size="sm" onClick={() => props.handleEditClick(props.row)}>
                    Edit
                  </Button>
                )}
                {!isRoot(props.row.type) && (
                  <Button size="sm" variant="danger" onClick={() => props.handleDeleteClick(props.row)}>
                    Delete
                  </Button>
                )}
                {props.index > 0 && (
                  <Button size="sm" onClick={() => props.handleSwapWithNeighbour(props.row, 'previous')}>
                    Move Up
                  </Button>
                )}
                {!props.isLast && (
                  <Button size="sm" onClick={() => props.handleSwapWithNeighbour(props.row, 'next')}>
                    Move Down
                  </Button>
                )}
                {isGroup(props.row.type) && (
                  <Button size="sm" onClick={() => props.handleCreateClick(props.row)}>
                    Create a new Child Item
                  </Button>
                )}
              </div>
            </div>
          </Popover.Body>
        </Popover>
      </Overlay>
      {/* 
      <a title="Edit" className="pointer" onClick={() => props.handleEditClick(props.row)}>
        <img src="/assets/icons/pencil.svg" />
      </a>
      <a title="Delete" className="pointer" onClick={() => props.handleDeleteClick(props.row)}>
        <img src="/assets/icons/trash.svg" />
      </a> */}
      {props.row.label}
    </span>
  );
};

interface UpsertTaskDefinitionModalProps {
  closeModal: () => void;
  currentNodeId?: number;
  institutionId: number;
  parentNode?: TaskDefnNode;
  show: boolean;
}

const UpsertTaskDefinitionModal = (props: UpsertTaskDefinitionModalProps) => {
  const isCreate = !props.currentNodeId;
  const record = useCacheRecord(TaskDefinition, props.currentNodeId, isCreate);
  const LocalTaskDefinition = useCloneEntity(TaskDefinition);
  const override = isCreate
    ? ({
        institution_id: props.institutionId,
        parent_definition_id: props.parentNode?.id,
        seq: props.parentNode ? props.parentNode.getMaxChildSeq() + 10 : 10,
      } as Partial<TaskDefinitionRow>)
    : undefined;

  return (
    <Modal
      show={props.show}
      onHide={props.closeModal}
      aria-labelledby="ref-value-picker-modal-title"
      size="xl"
      fullscreen="xl-down"
    >
      <Modal.Header closeButton>
        <Modal.Title id="ref-value-picker-modal-title">{isCreate ? 'Create' : 'Update'} Task Definition</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormManager
          compoundUrl="/main/~/upsert-task-definition"
          handleCancel={props.closeModal}
          handleSuccess={({ keyOfFirstCreate }) => props.closeModal()}
          initialization={{
            main: {
              type: isCreate ? 'create-single' : 'update-single',
              entity: LocalTaskDefinition,
              override,
              record: record,
            },
          }}
        >
          <NodeLogic entity={LocalTaskDefinition} />
          <FormBlock alias="main" entity={LocalTaskDefinition} isCreate={isCreate} />
        </FormManager>
      </Modal.Body>
    </Modal>
  );
};

const NodeLogic = ({ entity }: { entity: typeof TaskDefinition }): React.JSX.Element => {
  const { values } = useFormikContext<any>();
  const type = values?.main?.type;
  const isCreate = !values?.main?.id;
  const setOptionVisible = (optionId: string, active: boolean) => {
    const optionsField = entity.fields.type as OptionsField;
    const option = optionsField.options.find((opt) => opt.id === optionId);
    if (option) {
      option.active = active;
    }
  };
  setOptionVisible('ROOT', false);
  setOptionVisible('SEQUENTIAL-GROUP', isCreate || ['SEQUENTIAL-GROUP', 'PARALLEL-GROUP'].includes(type));
  setOptionVisible('PARALLEL-GROUP', isCreate || ['SEQUENTIAL-GROUP', 'PARALLEL-GROUP'].includes(type));
  setOptionVisible('APPLICATION-TASK', false);
  setOptionVisible('CHECK-TASK', isCreate || type === 'CHECK-TASK');
  entity.fields.id.visible = false;
  entity.fields.seq.visible = false;
  entity.fields.institution_id.visible = false;
  entity.fields.parent_definition_id.visible = false;
  entity.fields.markdown_text.visible = type === 'CHECK-TASK';
  return null;
};
